import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TableRowProps } from '../types';
import { CompareFunction } from '../types/compare-function';
import { Sort } from '../types/sort';

interface TableBodyProps<TData, TSort> {
	data: TData[] | undefined | null;
	render: React.FunctionComponent<TableRowProps<TData>>;
	EmptyTable: React.ReactNode;
	sort?: Sort<TSort>;
	sortFunction?: (sort: Sort<TSort>) => CompareFunction<TData>;
	lazy?: { initialItems: number; itemsPerPage: number };
	onAdvance?: {
        next?: () => void;
    };
	useMobileLayout: boolean;
	idProp?: keyof TData;
}

export function TableBody<TData, TSort>(props: TableBodyProps<TData, TSort>) {
	const { data, sortFunction, sort, lazy, onAdvance } = props;
	const { initialItems, itemsPerPage } = lazy ?? {
		initialItems: 20,
		itemsPerPage: 10,
	};

	const [currentSize, setCurrentSize] = useState(initialItems);
	const { rows, canAdvance } = useMemo(() => {
		if (!data)
			return {
				rows: [],
				canAdvance: false,
			};

		const sortedRows = data.map((x) => x);
		if (sortFunction && sort) {
			sortedRows.sort(sortFunction(sort));
		}

		return {
			rows: sortedRows.slice(0, currentSize),
			canAdvance: sortedRows.length > currentSize,
		};
	}, [data, currentSize, sortFunction, sort]);

	const advance = useCallback(() => {
		if (canAdvance) {
			setCurrentSize((prev) => prev + itemsPerPage);
		} else {
			onAdvance?.next?.();
		}
	}, [itemsPerPage, canAdvance, onAdvance]);

	const bottomBoundaryRef = useRef<HTMLTableRowElement>(null);
	useEffect(() => {
        const node = bottomBoundaryRef.current;
		if (node) {
			const observer = new IntersectionObserver((entries) => {
				entries.forEach((en) => {
					if (en.intersectionRatio > 0) {
						advance();
					}
				});
			});

			observer.observe(node);
			return () => observer.unobserve(node);
		}
	}, [advance]);

	const { render: Row, EmptyTable, useMobileLayout, idProp } = props;
	return (
		<>
			<tbody>
				{!rows || !rows.length
					? EmptyTable
					: rows.map((item, index, allItems) => (
							<Row
								key={item[idProp || ('id' as keyof TData)] as any}
								item={item}
								index={index}
								allItems={allItems}
								useMobileLayout={useMobileLayout}
							/>
					  ))}
			</tbody>
			<tfoot>
				<tr ref={bottomBoundaryRef}></tr>
			</tfoot>
		</>
	);
}
